import React, { useState } from "react";
import RemainingDaysBadge from "./common/RemainingDaysBadge";
import AdExtensionModal from "./AdExtensionModal";
import ShoppingKeywordEditModal from "./ShoppingKeywordEditModal";
import QuantityIncreaseModal from "./QuantityIncreaseModal";
import { rejectAdAPI, getMemberAdAPI } from "../../../../../api/index";
import { useAuth } from "../../../../../hooks/useAuth";
import "../AdDetailModal.css";

const ShoppingAdDetail = ({
  isOpen,
  onClose,
  adData,
  keywordData, // MyAdvertisement에서 전달받은 키워드 데이터
  priceGradeData, // MyAdvertisement에서 전달받은 가격 등급 데이터
  getUserDetailAPI, // MyAdvertisement에서 전달받은 사용자 상세 정보 API 함수
  onSave, // 키워드 수정 완료 시 호출될 콜백
}) => {
  const { user } = useAuth();
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [isKeywordEditModalOpen, setIsKeywordEditModalOpen] = useState(false);
  const [isQuantityIncreaseModalOpen, setIsQuantityIncreaseModalOpen] =
    useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isEnding, setIsEnding] = useState(false);

  // 1회 단가 계산 - 결제금액 ÷ 신청수량 (기존 광고의 실제 단가)
  const getUnitPrice = (adData) => {
    const totalPrice = adData.price || 0;
    const totalQuantity = adData.totalQuantity || 0;
    return totalQuantity > 0 ? Math.round(totalPrice / totalQuantity) : 0;
  };

  const unitPrices = {
    [adData.ad_id || adData.id]: getUnitPrice(adData),
  };

  // username은 adData에서 가져오기 (광고 소유자 정보)
  const username = adData.username || adData.member_id || "";

  // 가격 등급 데이터는 props로 받은 것 사용
  const userData = {
    price_grade_detail: {
      data: priceGradeData || [],
    },
  };

  if (!isOpen || !adData) return null;

  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "진행중";
      case "PENDING":
        return "승인대기중";
      case "REJECTED":
        return "거절";
      case "REFUND":
        return "환불";
      case "DONE":
        return "종료";
      default:
        return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "APPROVED":
        return "status-progress";
      case "PENDING":
        return "status-pending";
      case "REJECTED":
        return "status-cancelled";
      case "REFUND":
        return "status-refund";
      case "DONE":
        return "status-ended";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0].replace(/-/g, ".");
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString();
  };

  const TotalPrice = () => {
    const price = adData.price || 0;
    return formatNumber(price);
  };

  // 서브키워드 배열 생성 (모든 서브키워드 포함)
  const getSubKeywords = () => {
    // keywordData가 배열인 경우 SUB 타입 키워드들을 추출
    const subKeywords = keywordData?.filter((k) => k.type === "SUB") || [];
    return subKeywords.map((keyword) => keyword.name || "-");
  };

  // 광고 취소 핸들러
  const handleCancelAd = async () => {
    if (!adData?.ad_id) {
      alert("광고 정보를 찾을 수 없습니다.");
      return;
    }

    try {
      setIsCancelling(true);

      // 광고 상태 재확인
      const checkResponse = await getMemberAdAPI(
        user?.id || adData.webMemberId || adData.member_id,
        1,
        1,
        { ad_id: adData.ad_id }
      );

      const ads = checkResponse.data?.data || [];
      const currentAd = ads.find((ad) => ad.ad_id === adData.ad_id);

      if (!currentAd) {
        alert("광고를 찾을 수 없습니다.");
        setIsCancelling(false);
        return;
      }

      // status가 "PENDING"이 아니면 취소 불가
      if (currentAd.status !== "PENDING") {
        alert(
          `현재 광고 상태가 "${getStatusText(
            currentAd.status
          )}"이므로 등록 취소할 수 없습니다.`
        );
        setIsCancelling(false);
        // 상태가 변경되었을 수 있으므로 데이터 새로고침
        if (onSave) {
          onSave();
        }
        return;
      }

      const confirmCancel = window.confirm(
        "정말로 이 광고를 취소하시겠습니까?"
      );
      if (!confirmCancel) {
        setIsCancelling(false);
        return;
      }

      // rejectAdAPI 호출 (필요한 파라미터들 전달)
      const currentDeposit = user?.deposit || 0; // 로그인한 사용자의 현재 예치금
      const amount = currentAd.price || adData.price || 0;
      const newDeposit = currentDeposit + amount;

      await rejectAdAPI(
        currentAd.ad_id,
        currentAd.webMemberId || currentAd.member_id,
        currentAd.missionName || currentAd.adName || "광고",
        newDeposit,
        amount,
        "REJECTED",
        currentAd.startDate,
        currentAd.endDate
      );

      alert("광고가 성공적으로 취소되었습니다.");
      onClose(); // 모달 닫기
      // 부모 컴포넌트에서 데이터 새로고침을 위해 onSave 호출
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("광고 취소 실패:", error);
      alert("광고 취소에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsCancelling(false);
    }
  };

  // 광고 종료 핸들러
  const handleEndAd = async () => {
    if (!adData?.ad_id) {
      alert("광고 정보를 찾을 수 없습니다.");
      return;
    }

    // 오늘 날짜 계산 (로컬 시간 기준)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // 내일 날짜 계산
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // 날짜를 YYYY-MM-DD 형식으로 변환하는 함수
    const formatDateToString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const todayString = formatDateToString(today);

    // 광고 종료일
    const endDate = adData.endDate
      ? new Date(adData.endDate.split("T")[0])
      : null;

    if (!endDate) {
      alert("광고 종료일을 찾을 수 없습니다.");
      return;
    }

    // 내일부터 광고 종료일까지의 일수 계산
    const timeDiff = endDate.getTime() - tomorrow.getTime();
    const remainingDays = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24))); // 내일부터 종료일까지 일수

    // dailyQuantity 계산
    let dailyQuantity = adData.dailyQuantity || 0;
    if (
      !dailyQuantity &&
      adData.totalQuantity &&
      adData.startDate &&
      adData.endDate
    ) {
      // totalQuantity와 광고 기간으로 역산
      const startDate = new Date(adData.startDate.split("T")[0]);
      const endDateCalc = new Date(adData.endDate.split("T")[0]);
      const totalDays =
        Math.ceil(
          (endDateCalc.getTime() - startDate.getTime()) / (1000 * 3600 * 24)
        ) + 1;
      if (totalDays > 0) {
        dailyQuantity = Math.round(adData.totalQuantity / totalDays);
      }
    }

    // 1회 단가 계산
    const unitPrice = Math.round(adData.price / adData.totalQuantity) || 0;

    // 환불 금액 계산
    const refundAmount = dailyQuantity * remainingDays * unitPrice;

    // 날짜 포맷팅 함수 (YYYY-MM-DD -> YYYY.MM.DD)
    const formatDateForDisplay = (dateString) => {
      if (!dateString) return "-";
      return dateString.replace(/-/g, ".");
    };

    // 확인 팝업
    const confirmMessage = `광고 마감 날짜를 오늘(${formatDateForDisplay(
      todayString
    )})로 변경 후\n나머지 날짜의 광고 금액은 ${refundAmount.toLocaleString()}원입니다.\n\n광고를 중단하시겠습니까?`;

    const confirmEnd = window.confirm(confirmMessage);
    if (!confirmEnd) return;

    try {
      setIsEnding(true);

      // rejectAdAPI 호출 (status를 "DONE"으로 설정하여 광고 종료)
      const currentDeposit = user?.deposit || 0;
      const newDeposit = currentDeposit + refundAmount; // 환불 금액 추가

      await rejectAdAPI(
        adData.ad_id,
        adData.webMemberId || adData.member_id,
        adData.missionName || adData.adName || "광고",
        newDeposit,
        refundAmount, // 환불 금액
        "DONE",
        adData.startDate,
        todayString // 종료일을 오늘로 변경
      );

      alert("광고가 성공적으로 종료되었습니다.");
      onClose(); // 모달 닫기
      // 부모 컴포넌트에서 데이터 새로고침을 위해 onSave 호출
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("광고 종료 실패:", error);
      alert("광고 종료에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsEnding(false);
    }
  };

  return (
    <>
      <div className="ad-detail-modal-overlay" onClick={onClose}>
        <div className="ad-detail-modal" onClick={(e) => e.stopPropagation()}>
          {/* 헤더 - Flutter 구조 적용 */}
          <div className="modal-header">
            {/* Container */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                width: "100%",
              }}
            >
              {/* Row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                  flex: 1,
                }}
              >
                {/* 1-1-1. Image */}
                <div className="thumbnail-image">
                  <img
                    src={adData.imageUrl || "/logo192.png"}
                    alt={adData.missionName || "상품 이미지"}
                    className="product-thumbnail"
                    onError={(e) => {
                      e.target.src = "/logo192.png";
                    }}
                  />
                </div>

                {/* 1-1-2. Column */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    flex: 1,
                  }}
                >
                  {/* 1-1-2-1. Store Name */}
                  <div className="store-name">{adData.storeName || "-"}</div>

                  {/* 1-1-2-2. Mission Name */}
                  <div className="mission-name">
                    {adData.missionName || "-"}
                  </div>
                </div>
              </div>

              {/* 1-1-3. Remaining Days */}
              <div className="remaining-days">
                <RemainingDaysBadge
                  startDate={adData.startDate}
                  endDate={adData.endDate}
                  status={adData.status}
                />
              </div>

              {/* 1-1-4. Status */}
              <div className={`status-badge ${getStatusClass(adData.status)}`}>
                {getStatusText(adData.status)}
              </div>

              {/* 1-1-5. 광고연장버튼 (가장 우측에 고정) */}
              <button
                className="extension-btn"
                style={{ marginLeft: "auto" }}
                disabled={adData.status !== "APPROVED"}
                onClick={() => setIsExtensionModalOpen(true)}
              >
                광고연장
              </button>
            </div>
          </div>

          {/* 상세 내용 */}
          <div className="modal-content">
            <div className="form-grid">
              <div className="form-row">
                <label>카테고리</label>
                <div className="form-value">{adData.category || "-"}</div>
              </div>

              <div className="form-row">
                <label>스토어명</label>
                <div className="form-value">{adData.storeName || "-"}</div>
              </div>

              <div className="form-row">
                <label>광고명</label>
                <div className="form-value">{adData.missionName || "-"}</div>
              </div>

              <div className="form-row">
                <label>메인키워드</label>
                <div className="form-value">
                  {(() => {
                    const mainKeyword = keywordData?.find(
                      (k) => k.type === "MAIN"
                    );
                    return mainKeyword?.name || adData.mainKeyword || "-";
                  })()}
                </div>
              </div>

              <div className="form-row">
                <label>시작순위</label>
                <div className="form-value">
                  {(() => {
                    const mainKeyword = keywordData?.find(
                      (k) => k.type === "MAIN"
                    );
                    return (
                      mainKeyword?.initialRank ||
                      adData.initialRank ||
                      adData.startRank ||
                      "-"
                    );
                  })()}
                </div>
              </div>

              <div className="form-row">
                <label>현재순위</label>
                <div className="form-value">
                  {(() => {
                    const mainKeyword = keywordData?.find(
                      (k) => k.type === "MAIN"
                    );
                    return (
                      mainKeyword?.currentRank ||
                      adData.currentRank ||
                      adData.rank ||
                      "-"
                    );
                  })()}
                </div>
              </div>

              <div className="form-row">
                <label>광고 시작일</label>
                <div className="form-value">{formatDate(adData.startDate)}</div>
              </div>

              <div className="form-row">
                <label>광고 종료일</label>
                <div className="form-value">{formatDate(adData.endDate)}</div>
              </div>

              {getSubKeywords().map((keyword, index) => (
                <div className="form-row" key={index}>
                  <label>서브키워드{index + 1}</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={keyword}
                  >
                    {keyword}
                  </div>
                </div>
              ))}

              <div className="form-row">
                <label>1회 단가</label>
                <div
                  className="form-value"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={`${formatNumber(
                    Math.round(adData.price / adData.totalQuantity)
                  )}원`}
                >
                  {formatNumber(
                    Math.round(adData.price / adData.totalQuantity)
                  )}
                  원
                </div>
              </div>

              <div className="form-row">
                <label>신청 수량</label>
                <div
                  className="form-value"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={formatNumber(adData.totalQuantity)}
                >
                  {formatNumber(adData.totalQuantity)}
                </div>
              </div>

              <div className="form-row">
                <label>결제 금액</label>
                <div
                  className="form-value"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={`${TotalPrice()}원`}
                >
                  {TotalPrice()}원
                </div>
              </div>

              <div className="form-row">
                <label>마감 예정일</label>
                <div
                  className="form-value"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={formatDate(adData.endDate)}
                >
                  {formatDate(adData.endDate)}
                </div>
              </div>
            </div>
          </div>

          {/* 닫기 버튼 */}
          <div className="modal-footer">
            {(adData.status === "APPROVED" || adData.status === "PENDING") && (
              <button
                className="ad-detail-edit-keyword-btn"
                onClick={() => {
                  setIsKeywordEditModalOpen(true);
                }}
              >
                키워드 수정
              </button>
            )}
            {(adData.status === "APPROVED" || adData.status === "PENDING") && (
              <button
                className="ad-detail-increase-quantity-btn"
                onClick={() => {
                  setIsQuantityIncreaseModalOpen(true);
                }}
              >
                수량 증가
              </button>
            )}
            {adData.status === "PENDING" && (
              <button
                className="ad-detail-cancel-btn"
                onClick={handleCancelAd}
                disabled={isCancelling}
                // style={{
                //   backgroundColor: "#dc3545",
                //   color: "white",
                //   border: "none",
                //   padding: "8px 16px",
                //   borderRadius: "4px",
                //   cursor: isCancelling ? "not-allowed" : "pointer",
                //   opacity: isCancelling ? 0.6 : 1,
                // }}
              >
                {isCancelling ? "취소 중..." : "등록 취소"}
              </button>
            )}
            {adData.status === "APPROVED" && (
              <button
                className="ad-detail-end-btn"
                onClick={handleEndAd}
                disabled={isEnding}
              >
                {isEnding ? "종료 중..." : "광고 종료"}
              </button>
            )}
            <button className="ad-detail-close-btn" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>
      </div>

      {/* 광고 연장 모달을 최상위 레벨로 렌더링 */}
      {isExtensionModalOpen && (
        <AdExtensionModal
          isOpen={isExtensionModalOpen}
          onClose={() => setIsExtensionModalOpen(false)}
          selectedAds={[adData]}
          onExtend={(result) => {
            if (result === "success") {
              // 연장 성공 시 모달 닫기
              setIsExtensionModalOpen(false);
              // 부모 컴포넌트에 연장 완료 알림 및 데이터 새로고침
              alert("광고 연장이 성공적으로 완료되었습니다!");
              // 부모 컴포넌트의 데이터 새로고침 함수 호출
              if (onSave) {
                onSave();
              }
            }
          }}
          unitPrices={unitPrices} // unitPrices 데이터 전달
          username={username} // username 데이터 전달
          user={userData} // 가격 등급 데이터가 포함된 userData 전달
          getUserDetailAPI={getUserDetailAPI} // API 함수 전달
        />
      )}

      {/* 키워드 수정 모달 */}
      {isKeywordEditModalOpen && (
        <ShoppingKeywordEditModal
          isOpen={isKeywordEditModalOpen}
          onClose={() => setIsKeywordEditModalOpen(false)}
          adData={adData}
          keywordData={keywordData}
          onSave={onSave}
        />
      )}

      {/* 수량 증가 모달 */}
      {isQuantityIncreaseModalOpen && (
        <QuantityIncreaseModal
          isOpen={isQuantityIncreaseModalOpen}
          onClose={() => setIsQuantityIncreaseModalOpen(false)}
          adData={adData}
          unitPrice={getUnitPrice(adData)}
          onSave={onSave}
        />
      )}
    </>
  );
};

export default ShoppingAdDetail;
